// @ds-allow-hex: o `appearance` do Clerk é config de um SDK TERCEIRO — só aceita
// hex literal, não resolve `var(--…)`. Os valores abaixo ESPELHAM os tokens do MDS;
// se um token mudar, este bloco muda com ele. É a única cópia autorizada da paleta.
import { ClerkProvider, useAuth } from '@clerk/clerk-react';
import { ptPT } from '@clerk/localizations';
import { useTheme } from '@marcianus/design/components';
import { type ReactNode, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { env } from '@/lib/env';
import { setTokenGetter } from '@/services/http';

// Liga o token de sessão do Clerk à camada http. Organizador e participante
// autenticam via Clerk (base de utilizadores real desde o dia 1).
function TokenBridge({ children }: { children: ReactNode }) {
  const { getToken, isSignedIn, isLoaded } = useAuth();
  const qc = useQueryClient();
  const wasSignedIn = useRef(false);

  useEffect(() => {
    setTokenGetter(() => getToken());
  }, [getToken]);

  // RACE FIX: na 1.ª montagem, queries autenticadas (ex.: /users/me) podem
  // disparar ANTES de o token estar anexado → 401 cacheado, e com `retry:false`
  // ficam mortas (era isto que matava o gate de onboarding: `me` nunca chegava).
  // Quando o Clerk assenta como AUTENTICADO, invalidamos tudo uma vez → refetch
  // já com o token. Só na transição guest→signed-in, não em cada render.
  useEffect(() => {
    if (isLoaded && isSignedIn && !wasSignedIn.current) {
      wasSignedIn.current = true;
      qc.invalidateQueries();
    }
    if (isLoaded && !isSignedIn) wasSignedIn.current = false;
  }, [isLoaded, isSignedIn, qc]);

  return <>{children}</>;
}

// Aparência Marcianus (MDS) para todos os ecrãs Clerk (login, perfil, UserButton).
// Segue o TEMA da app (claro por omissão): um modal escuro sobre uma app clara
// era a maior quebra de consistência visual do fluxo de entrada.
const PALETTES = {
  light: { bg: '#FFFFFF', card: '#FFFFFF', text: '#17132B', muted: '#5A5476', input: '#FAF9FD', border: '#E2DEF2', link: '#5238BC', glow: 'rgba(93,63,211,.28)', shadow: '0 24px 60px -24px rgba(40,24,110,.35)', primary: '#5D3FD3' },
  dark: { bg: '#0e0b18', card: '#141024', text: '#f4f2fb', muted: '#9a92b6', input: '#080611', border: '#241d3d', link: '#9a86ff', glow: 'rgba(123,97,255,.25)', shadow: '0 24px 60px -20px rgba(0,0,0,.7)', primary: '#7B61FF' },
};

function buildAppearance(mode: 'light' | 'dark') {
  const c = PALETTES[mode];
  return {
    layout: { logoImageUrl: '/assets/symbol.png', logoPlacement: 'inside' as const, socialButtonsVariant: 'blockButton' as const },
    variables: {
      colorPrimary: c.primary, colorBackground: c.bg, colorText: c.text, colorTextSecondary: c.muted,
      colorInputBackground: c.input, colorInputText: c.text, colorNeutral: c.text,
      borderRadius: '12px', fontFamily: "'Satoshi','Inter',-apple-system,sans-serif",
    },
    elements: {
      card: { background: c.card, border: `1px solid ${c.border}`, boxShadow: c.shadow },
      headerTitle: { color: c.text }, headerSubtitle: { color: c.muted },
      socialButtonsBlockButton: { background: c.input, border: `1px solid ${c.border}`, color: c.text, minHeight: 44 },
      formButtonPrimary: { background: c.primary, color: '#fff', boxShadow: `0 0 20px ${c.glow}`, minHeight: 44 },
      footerActionLink: { color: c.link }, dividerLine: { background: c.border },
      formFieldInput: { border: `1px solid ${c.border}`, minHeight: 44, fontSize: 16 /* ≥16px: o iOS não faz zoom ao focar */ },
    },
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  // Modo preview (Tailscale): sem Clerk. O domínio não está autorizado na chave
  // live, e getToken() ficaria pendurado → todos os pedidos travariam. Convidado.
  const { theme } = useTheme();
  if (env.preview) return <>{children}</>;
  return (
    <ClerkProvider publishableKey={env.clerkPublishableKey} appearance={buildAppearance(theme)} localization={ptPT}>
      <TokenBridge>{children}</TokenBridge>
    </ClerkProvider>
  );
}
